import { emptySplitApi as api } from "./baseApi";

export const addTagTypes = ["Dashboard", "DashboardLayout", "DataSource"] as const;

// ==================== Types ====================

export interface WidgetPosition {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface WidgetDisplayOptions {
  showLegend?: boolean;
  colorScheme?: string;
  decimalPlaces?: number;
  prefix?: string;
  suffix?: string;
  backgroundColor?: string;
  textColor?: string;
}

export interface WidgetConfig {
  metric?: string;
  aggregation?: 'sum' | 'avg' | 'count' | 'min' | 'max';
  groupBy?: string;
  filters?: Record<string, any>;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  unit?: string;
  displayOptions?: WidgetDisplayOptions;
  timeGranularity?: 'hour' | 'day' | 'week' | 'month' | 'quarter' | 'year';
  includePreviousPeriod?: boolean;
  // Gauge widget specific
  max?: number;
  min?: number;
  thresholds?: { value: number; color: string }[];
}

export type WidgetType = 
  | 'scorecard' 
  | 'piechart' 
  | 'linegraph' 
  | 'bargraph' 
  | 'table' 
  | 'gauge'
  | 'areachart'
  | 'donutchart';

export interface DashboardWidget {
  id: string;
  name: string;
  type: WidgetType;
  position: WidgetPosition;
  dataSource: string;
  config: WidgetConfig;
  description?: string;
}

export interface DashboardPermissions {
  roles: string[];
  users: string[];
}

export interface DashboardMetadata {
  tags?: string[];
  icon?: string;
  color?: string;
  category?: string;
}

export interface ViewSettings {
  fontSize?: number;
  defaultTimeFrame?: string;
}

export interface Dashboard {
  id: string;
  name: string;
  description?: string;
  layoutId: string;
  widgets: DashboardWidget[];
  refreshInterval: number;
  isPublished: boolean;
  createdBy: string;
  permissions: DashboardPermissions;
  metadata?: DashboardMetadata;
  viewSettings?: ViewSettings;
  createdAt: string;
  updatedAt: string;
}

export interface LayoutZone {
  id: string;
  name: string;
  x: number;
  y: number;
  width: number;
  height: number;
  allowedWidgetTypes?: string[];
}

export interface GridDefinition {
  columns: number;
  rows: number;
  gap: number;
  rowHeight: number;
  zones: LayoutZone[];
}

export interface DashboardLayout {
  id: string;
  name: string;
  description?: string;
  gridDefinition: GridDefinition;
  maxWidgets: number;
  previewConfig?: {
    thumbnail?: string;
    previewLayout: { id: string; x: number; y: number; width: number; height: number; label?: string }[];
  };
  isActive: boolean;
  sortOrder: number;
  category?: string;
}

export interface MetricDefinition {
  id: string;
  name: string;
  description?: string;
  type: 'number' | 'currency' | 'percentage' | 'count' | 'duration';
  aggregations: string[];
  defaultAggregation?: string;
  unit?: string;
  supportsTimeSeries?: boolean;
}

export interface DimensionDefinition {
  id: string;
  name: string;
  description?: string;
  type: 'categorical' | 'temporal' | 'hierarchical' | 'geographical';
  values?: string[];
  dynamicValues?: boolean;
}

export interface DataSource {
  id: string;
  name: string;
  description: string;
  icon?: string;
  category?: string;
  metrics: MetricDefinition[];
  dimensions: DimensionDefinition[];
}

export interface WidgetDataResult {
  data: any;
  metadata?: {
    totalCount?: number;
    queryTime?: number;
    cached?: boolean;
    error?: string;
    unit?: string;
    metricType?: string;
  };
  previousPeriod?: any;
  trend?: {
    direction: 'up' | 'down' | 'flat';
    percentage: number;
  };
}

// ==================== API Endpoints ====================

const wallboardApi = api
  .enhanceEndpoints({ addTagTypes })
  .injectEndpoints({
    endpoints: (build) => ({
      // ==================== Dashboard CRUD ====================
      
      getDashboards: build.query<Dashboard[], { publishedOnly?: boolean; includeOwn?: boolean } | void>({
        query: (args) => ({
          url: '/dashboards',
          params: {
            publishedOnly: args?.publishedOnly,
            includeOwn: args?.includeOwn,
          },
        }),
        providesTags: (result) =>
          result
            ? [
                ...result.map(({ id }) => ({ type: 'Dashboard' as const, id })),
                { type: 'Dashboard', id: 'LIST' },
              ]
            : [{ type: 'Dashboard', id: 'LIST' }],
      }),

      getDashboardById: build.query<Dashboard, string>({
        query: (id) => ({ url: `/dashboards/${id}` }),
        providesTags: (result, error, id) => [{ type: 'Dashboard', id }],
      }),

      createDashboard: build.mutation<Dashboard, Partial<Dashboard>>({
        query: (body) => ({
          url: '/dashboards',
          method: 'POST',
          body,
        }),
        invalidatesTags: [{ type: 'Dashboard', id: 'LIST' }],
      }),

      updateDashboard: build.mutation<Dashboard, { id: string; body: Partial<Dashboard> }>({
        query: ({ id, body }) => ({
          url: `/dashboards/${id}`,
          method: 'PUT',
          body,
        }),
        invalidatesTags: (result, error, { id }) => [
          { type: 'Dashboard', id },
          { type: 'Dashboard', id: 'LIST' },
        ],
      }),

      deleteDashboard: build.mutation<void, string>({
        query: (id) => ({
          url: `/dashboards/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, id) => [
          { type: 'Dashboard', id },
          { type: 'Dashboard', id: 'LIST' },
        ],
      }),

      // ==================== Permissions ====================

      updateDashboardPermissions: build.mutation<
        DashboardPermissions,
        { id: string; permissions: Partial<DashboardPermissions> }
      >({
        query: ({ id, permissions }) => ({
          url: `/dashboards/${id}/permissions`,
          method: 'POST',
          body: permissions,
        }),
        invalidatesTags: (result, error, { id }) => [{ type: 'Dashboard', id }],
      }),

      // ==================== Data Fetching ====================

      fetchDashboardData: build.mutation<
        { widgets: Record<string, WidgetDataResult>; fetchedAt: string },
        {
          dashboardId: string;
          widgetIds?: string[];
          timeRange?: { start: string; end: string };
        }
      >({
        query: ({ dashboardId, ...body }) => ({
          url: `/dashboards/${dashboardId}/data`,
          method: 'POST',
          body,
        }),
      }),

      fetchDrillDown: build.mutation<
        { records: any[]; total: number; columns: { id: string; label: string }[] },
        {
          dashboardId: string;
          widgetId: string;
          drillDownValue?: string;
          timeRange?: { start: string; end: string };
          limit?: number;
        }
      >({
        query: ({ dashboardId, ...body }) => ({
          url: `/dashboards/${dashboardId}/drilldown`,
          method: 'POST',
          body,
        }),
      }),

      // ==================== Layouts ====================

      getLayouts: build.query<DashboardLayout[], void>({
        query: () => ({ url: '/dashboard-layouts' }),
        providesTags: [{ type: 'DashboardLayout', id: 'LIST' }],
      }),

      getLayoutById: build.query<DashboardLayout, string>({
        query: (id) => ({ url: `/dashboard-layouts/${id}` }),
        providesTags: (result, error, id) => [{ type: 'DashboardLayout', id }],
      }),

      // ==================== Data Sources ====================

      getDataSources: build.query<DataSource[], void>({
        query: () => ({ url: '/dashboard-data-sources' }),
        providesTags: [{ type: 'DataSource', id: 'LIST' }],
      }),

      getDataSourceMetrics: build.query<MetricDefinition[], string>({
        query: (id) => ({ url: `/dashboard-data-sources/${id}/metrics` }),
        providesTags: (result, error, id) => [{ type: 'DataSource', id: `${id}-metrics` }],
      }),

      getDataSourceDimensions: build.query<DimensionDefinition[], string>({
        query: (id) => ({ url: `/dashboard-data-sources/${id}/dimensions` }),
        providesTags: (result, error, id) => [{ type: 'DataSource', id: `${id}-dimensions` }],
      }),

      getDimensionValues: build.query<
        { value: string; label: string }[],
        { sourceId: string; dimensionId: string }
      >({
        query: ({ sourceId, dimensionId }) => ({
          url: `/dashboard-data-sources/${sourceId}/dimensions/${dimensionId}/values`,
        }),
      }),
    }),
    overrideExisting: true,
  });

// ==================== Exported Hooks ====================

export const {
  // Dashboard CRUD
  useGetDashboardsQuery,
  useGetDashboardByIdQuery,
  useCreateDashboardMutation,
  useUpdateDashboardMutation,
  useDeleteDashboardMutation,
  
  // Permissions
  useUpdateDashboardPermissionsMutation,
  
  // Data fetching
  useFetchDashboardDataMutation,
  useFetchDrillDownMutation,
  
  // Layouts
  useGetLayoutsQuery,
  useGetLayoutByIdQuery,
  
  // Data sources
  useGetDataSourcesQuery,
  useGetDataSourceMetricsQuery,
  useGetDataSourceDimensionsQuery,
  useGetDimensionValuesQuery,
} = wallboardApi;

export default wallboardApi;
